import { createSlice } from "@reduxjs/toolkit";
import { marketplaceSaga } from "./saga";
import { useInjectReducer } from "utils/@reduxjs/useInjectReducer";
import { useInjectSaga } from "utils/@reduxjs/useInjectSaga";
import { useMemo } from "react";

export const initialState = {
  marketplace: {
    initialLoading: false,
    updateLoading: false,
    statusUpdateLoading: false,
    dropdownLoading: false,
    loading: false,
    error: null,

    pending_approvals: 0,
    approval_requests: 0,
    rejected_requests: 0,
    filtered_pending_approvals: 0,
    filtered_approval_requests: 0,
    filtered_rejected_requests: 0,

    marketplaces: [],
    selectedMarketplace: null,

    filtered_count: 0,
    pagination: {},

    status: "",
    period: "",
    service: "",
    search: "",
    sort_by: "",
    sort_order: ""
  },
  marketplaceUser: {
    loading: false,
    error: null,
    data: null
  },
  createCampaign: {
    loading: false,
    error: null,
    success: false,
    data: null
  },
  campaignReport: {
    initialLoading: true,
    loading: false,
    error: null,
    reports: [],
    status_counts: {},
    poster_counts: {
      poster_total_campaigns: 0,
      poster_total_reported_campaigns: 0
    },
    posterCountsLoading: false,
    pagination: {},
    updateLoading: false,
    updateSuccess: false // <- add
  },
  marketplaceApproval: {
    loading: false,
    error: null,
    data: null
  }
};

export const MarketplaceSlice = createSlice({
  name: "marketplace",
  initialState,
  reducers: {
    fetchMarketplace: (state, action) => {
      const payload = action.payload || {}; // ensure it's at least {}
      state.marketplace.loading = true;
      if (state.marketplace.initialLoading) {
        state.marketplace.initialLoading = true;
      }
      state.marketplace.error = null;
      if (payload.status) state.marketplace.status = payload.status;
      if (payload.period) state.marketplace.period = payload.period;
      if (payload.service) state.marketplace.service = payload.service;
      if (payload.search) state.marketplace.search = payload.search;
      if (payload.sort_by) state.marketplace.sort_by = payload.sort_by;
      if (payload.sort_order) state.marketplace.sort_order = payload.sort_order;
    },

    fetchMarketplaceSuccess: (state, action) => {
      const {
        pending_approvals,
        approval_requests,
        rejected_requests,
        filtered_pending_approvals,
        filtered_approval_requests,
        filtered_rejected_requests,
        marketplaces,
        filtered_count,
        pagination
      } = action.payload;
      state.marketplace.loading = false;
      state.marketplace.initialLoading = false;
      state.marketplace.error = null;
      state.marketplace.pending_approvals = pending_approvals;
      state.marketplace.approval_requests = approval_requests;
      state.marketplace.rejected_requests = rejected_requests;
      state.marketplace.filtered_pending_approvals = filtered_pending_approvals;
      state.marketplace.filtered_approval_requests = filtered_approval_requests;
      state.marketplace.filtered_rejected_requests = filtered_rejected_requests;
      state.marketplace.marketplaces = marketplaces;
      state.marketplace.filtered_count = filtered_count;
      state.marketplace.pagination = pagination;
    },
    fetchMarketplaceFailure: (state, action) => {
      state.marketplace.loading = false;
      state.marketplace.initialLoading = false;
      state.marketplace.error = action.payload;
    },

    // GET MARKETPLACE BY ID
    getMarketplaceById: (state) => {
      state.marketplace.loading = true;
      state.marketplace.error = null;
    },
    getMarketplaceByIdSuccess: (state, action) => {
      state.marketplace.loading = false;
      state.marketplace.selectedMarketplace = action.payload;
    },

    getMarketplaceByIdFailure: (state, action) => {
      state.marketplace.loading = false;
      state.marketplace.error = action.payload;
    },

    // update
    updateMarketplace: (state) => {
      state.marketplace.updateLoading = true;
      state.marketplace.error = null;
    },
    updateMarketplaceSuccess: (state, action) => {
      let updatedItem = action.payload;

      // Convert Both to Instagram, YouTube for table display
      if (updatedItem.platform === "Both") {
        updatedItem = { ...updatedItem, platform: "Instagram, YouTube" };
      }

      state.marketplace.updateLoading = false;
      state.marketplace.error = null;

      state.marketplace.marketplaces = state.marketplace.marketplaces.map(
        (item) => (item.id === updatedItem.id ? updatedItem : item)
      );
      if (
        state.marketplace.selectedMarketplace &&
        state.marketplace.selectedMarketplace.id === updatedItem.id
      ) {
        state.marketplace.selectedMarketplace = updatedItem;
      }
    },

    updateMarketplaceFailure: (state, action) => {
      state.marketplace.updateLoading = false;
      state.marketplace.error = action.payload;
    },

    getMarketplaceDropdownOptionsFailure: (state, action) => {
      state.marketplace.dropdownLoading = false;
      state.marketplace.error = action.payload;
    },

    // CREATE - CAMPAIGN USER - DETAILS
    fetchMarketplaceUser: (state) => {
      state.marketplaceUser.loading = true;
      state.marketplaceUser.error = null;
    },
    fetchMarketplaceUserSuccess: (state, action) => {
      state.marketplaceUser.loading = false;
      state.marketplaceUser.data = action.payload;
      state.marketplaceUser.error = null;
    },
    fetchMarketplaceUserError: (state, action) => {
      state.marketplaceUser.loading = false;
      state.marketplaceUser.error = action.payload;
    },

    // CREATE CAMPAIGN
    createMarketplaceCampaign: (state) => {
      state.createCampaign.loading = true;
      state.createCampaign.error = null;
      state.createCampaign.success = false;
    },
    createMarketplaceCampaignSuccess: (state, action) => {
      state.createCampaign.loading = false;
      state.createCampaign.success = true;
      state.createCampaign.data = action.payload;
      state.createCampaign.error = null;
    },
    createMarketplaceCampaignFailure: (state, action) => {
      state.createCampaign.loading = false;
      state.createCampaign.error = action.payload;
      state.createCampaign.success = false;
    },

    // CAMPAIGN REPORT
    fetchCampaignReports: (state, action) => {
      state.campaignReport.loading = true;
      state.campaignReport.error = null;
    },
    fetchCampaignReportsSuccess: (state, action) => {
      state.campaignReport.loading = false;
      state.campaignReport.initialLoading = false;
      state.campaignReport.reports = action.payload.reports;
      state.campaignReport.status_counts = action.payload.status_counts ?? {};
      state.campaignReport.pagination = action.payload.pagination;
      state.campaignReport.error = null;
    },
    fetchCampaignReportsFailure: (state, action) => {
      state.campaignReport.loading = false;
      state.campaignReport.initialLoading = false;
      state.campaignReport.error = action.payload;
    },

    fetchPosterCounts: (state) => {
      state.campaignReport.posterCountsLoading = true;
      state.campaignReport.error = null;
    },
    fetchPosterCountsSuccess: (state, action) => {
      state.campaignReport.posterCountsLoading = false;
      state.campaignReport.poster_counts = action.payload.poster_counts;
    },
    fetchPosterCountsFailure: (state, action) => {
      state.campaignReport.posterCountsLoading = false;
      state.campaignReport.error = action.payload;
    },
    updateCampaignReportStatus: (state) => {
      state.campaignReport.updateLoading = true;
      state.campaignReport.updateSuccess = false; // reset on new request
      state.campaignReport.error = null;
    },
    updateCampaignReportStatusSuccess: (state, action) => {
      state.campaignReport.updateLoading = false;
      state.campaignReport.updateSuccess = true; // <- set true

      // update table row immediately without reload
      state.campaignReport.reports = state.campaignReport.reports.map(
        (report) =>
          report.id === action.payload.reportId
            ? { ...report, report_status: action.payload.report_status }
            : report
      );
    },
    updateCampaignReportStatusFailure: (state, action) => {
      state.campaignReport.updateLoading = false;
      state.campaignReport.updateSuccess = false;
      state.campaignReport.error = action.payload;
    },

    // reset flag after modal closes
    resetUpdateSuccess: (state) => {
      state.campaignReport.updateSuccess = false;
    },

    updateMarketplaceRequestStatus: (state) => {
      state.marketplace.statusUpdateLoading = true;
      state.marketplace.error = null;
    },

    updateMarketplaceRequestStatusSuccess: (state) => {
      state.marketplace.statusUpdateLoading = false;
      state.marketplace.error = null;
    },

    updateMarketplaceRequestStatusFailure: (state, action) => {
      state.marketplace.statusUpdateLoading = false;
      state.marketplace.error = action.payload;
    },

    fetchMarketplaceApprovalDetails: (state) => {
      state.marketplaceApproval.loading = true;
      state.marketplaceApproval.error = null;
    },

    fetchMarketplaceApprovalDetailsSuccess: (state, action) => {
      state.marketplaceApproval.loading = false;
      state.marketplaceApproval.data = action.payload;
      state.marketplaceApproval.error = null;
    },

    fetchMarketplaceApprovalDetailsFailure: (state, action) => {
      state.marketplaceApproval.loading = false;
      state.marketplaceApproval.error = action.payload;
    }
  }
});

export const useMarketplaceSlice = () => {
  useInjectReducer(MarketplaceSlice.name, MarketplaceSlice.reducer);
  useInjectSaga(MarketplaceSlice.name, marketplaceSaga);
  const actions = useMemo(() => MarketplaceSlice.actions, []);
  return { actions };
};

export const { actions: marketplaceActions } = MarketplaceSlice;
export default MarketplaceSlice.reducer;
